<template>
  <div class="pp-stack" data-test="pp-tab-tasks">
    <PanelCard title="Tasks" icon="fa-check-square-o" test-id="pp-tasks">
      <p v-if="listState === 'loading'" class="pp-empty" data-test="pp-tasks-loading">Loading tasks…</p>
      <p v-else-if="listState === 'failed'" class="pp-empty" data-test="pp-tasks-failed">Couldn't load tasks right now.</p>

      <div v-else class="pp-tasks">
        <TasksSection
          section-key="open"
          title="Open"
          collapsible
          :rows="split.open"
          :ctx="ctx"
          :employees="employees"
          show-due
          empty-text="No open tasks for this patient."
          @open="openTask"
        />
        <TasksSection
          v-if="split.completed.length"
          section-key="completed"
          title="Completed"
          collapsible
          start-collapsed
          dot="done"
          :rows="split.completed"
          :ctx="ctx"
          :employees="employees"
          @open="openTask"
        />
      </div>
    </PanelCard>
  </div>
</template>

<script>
/**
 * Tasks — Teamio tasks tagged with this patient, drawn with the Tasks page's own rows
 * (Patient Profile overhaul, 2026-10-09). ONE tab for both surfaces: the new Patient Profile page and
 * the Schedule View patient panel.
 *
 *   rows      TaskRow (src/pages/TaskList/components/) inside TasksSection — an "Open" section (count,
 *             soonest due first, due day, assignee initials) and a folded "Completed" one (struck
 *             through, as the Tasks page draws done tasks).
 *   a click   opens the SAME TaskDrawer the Tasks page opens (540 wide, from the right, the same props)
 *             through the host page's single <slideout-panel> outlet.
 *   the check TaskRow completes the task itself, with the Tasks list's own dispatches. Nothing here
 *             re-implements it.
 *
 * Where the tasks come from: the host. Both hosts keep ONE Firestore listener on the shared slot
 * `state.tasks.tasksTaggedWithPatient` (the page: PatientTasksListener.vue; the panel: the patientPanel
 * store) and pass its rows in as `tasks`. A drawer save or a completion writes to Firestore, the
 * listener fires on the local write, the slot changes and the rows follow — there is no list here to
 * patch. (ToDo.vue patches its own lists from the drawer's `onTaskUpdated`; that hands back the drawer's
 * copy of the task, a different shape from this slot's, so it is deliberately NOT wired here.)
 *
 * The pure rules (order, ctx, tag list, drawer width) are src/utils/patientPanel/patientTasks.js.
 *
 * ⚠️ KNOWN LIMIT — the demo mask does not reach this tab. Tasks come from Firestore, which bypasses
 * the axios boundary where masking happens, and task text often names a patient. Patient Profile's
 * Tasks card has the same limit; Schedule View's `task` alert avoids it by showing only a count.
 *
 * ⛔ No prop named `patNum`, `siteID`, `loading` or `allSettings` — mixin.js computeds silently
 * replace them. This tab does not use the mixin; it reads the signed-in user, site and patient roster
 * straight from state.customer, like TaskDrawer.
 */
import PanelCard from '../PanelCard.vue';
import TasksSection from '@/pages/TaskList/components/TasksSection.vue';
import TaskDrawer from '@/pages/TaskList/components/TaskDrawer.vue';
import {
  TASK_DRAWER_WIDTH, splitPatientTasks, buildTaskCtx, drawerSiteTags,
} from '@/utils/patientPanel/patientTasks.js';

export default {
  name: 'TasksTab',

  components: { PanelCard, TasksSection },

  props: {
    /** The patient's tasks (state.tasks.tasksTaggedWithPatient, already filtered to this patient). */
    tasks: { type: Array, default: () => [] },
    /** customer.employees, for assignee initials. */
    employees: { type: Array, default: () => [] },
    /** The host's 'YYYY-MM-DD' for the site; falls back to now in `timezone`. */
    today: { type: String, default: '' },
    /** The site's IANA timezone (GENERAL_TIMEZONE). */
    timezone: { type: String, default: '' },
    /** 'ready' | 'loading' | 'failed' — the page's listener reports it; the panel has no such signal. */
    listState: { type: String, default: 'ready' },
  },

  data() {
    return { wikiPages: [] };
  },

  computed: {
    customerState() {
      return (this.$store && this.$store.state && this.$store.state.customer) || {};
    },

    ctx() {
      // knownTasks: TaskRow's repeat-task duplicate check otherwise only sees the Tasks page's lists, which
      // are empty until that page has been visited. A repeating task's next instance carries the same
      // patient tag, so THIS patient's tasks are where it would be.
      return {
        ...buildTaskCtx({ userID: this.customerState.userID, today: this.today, timezone: this.timezone }),
        knownTasks: this.tasks,
      };
    },

    split() {
      return splitPatientTasks(this.tasks, this.ctx);
    },
  },

  methods: {
    /**
     * ToDo.vue tkOpen, for this list: the wiki pages (fetched once, if not loaded yet), the patient
     * roster and the site's tags go in; the drawer reads the full task + comments itself on mount.
     */
    async openTask(task) {
      if (!task || !task.id) return;
      if (!this.wikiPages || this.wikiPages.length === 0) {
        try {
          const results = await this.$store.dispatch('wiki/getWikiPages', { siteID: this.customerState.siteID });
          if (results && results.length > 0) this.wikiPages = results[0] || [];
        } catch (error) {
          console.error('[Patient tasks] wiki pages could not be loaded before opening the task:', error);
          this.wikiPages = [];
        }
      }
      const tasksState = (this.$store.state && this.$store.state.tasks) || {};
      this.$showPanel({
        component: TaskDrawer,
        openOn: 'right',
        width: TASK_DRAWER_WIDTH,
        props: {
          task: Object.assign({}, task),
          wikiPages: this.wikiPages,
          patients: this.customerState.patients || [],
          siteTags: drawerSiteTags(tasksState.siteTaskTags, this.tasks),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// The Tasks page's sections carry a page-sized top margin; inside this card the first one starts at
// the card's own padding.
.pp-tasks ::v-deep .tk-sec:first-child { margin-top: 0; }
</style>
